import { Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export enum ToasterType {
  Default = 'DEFAULT',
  Info = 'INFO',
  Error = 'ERROR',
  Warning = 'WARNING',
  Success = 'SUCCESS',
}

type ToasterStyle = {
  border: string;
  background: string;
  title: string;
  supportingText: string;
  icon: string;
  iconOutline: string;
  iconBorder: string;
  closeIcon: string;
};

const TOASTER_STYLES: Record<ToasterType, ToasterStyle> = {
  [ToasterType.Default]: {
    border: 'border-[#CDD5DF]',
    background: 'bg-[#FCFCFD]',
    title: 'text-[#364152]',
    supportingText: 'text-[#4B5565]',
    icon: 'text-[#4B5565]',
    iconOutline: 'outline-[#4B5565]/20',
    iconBorder: 'border-[#4B5565]',
    closeIcon: 'text-[#697586]',
  },
  [ToasterType.Info]: {
    border: 'border-[#B3CAE4]',
    background: 'bg-[#E9F0F7]',
    title: 'text-[#2562A9]',
    supportingText: 'text-[#2562A9]',
    icon: 'text-[#477EBE]',
    iconOutline: 'outline-[#477EBE]/20',
    iconBorder: 'border-[#477EBE]',
    closeIcon: 'text-[#6895C9]',
  },
  [ToasterType.Error]: {
    border: 'border-[#E38D98]',
    background: 'bg-[#F9E8EA]',
    title: 'text-[#A91729]',
    supportingText: 'text-[#A91729]',
    icon: 'text-[#C71B30]',
    iconOutline: 'outline-[#C71B30]/20',
    iconBorder: 'border-[#C71B30]',
    closeIcon: 'text-[#CD3245]',
  },
  [ToasterType.Warning]: {
    border: 'border-[#F8B673]',
    background: 'bg-[#FEF2E6]',
    title: 'text-[#CE6900]',
    supportingText: 'text-[#CE6900]',
    icon: 'text-[#EA7700]',
    iconOutline: 'outline-[#EA7700]/20',
    iconBorder: 'border-[#EA7700]',
    closeIcon: 'text-[#F48F26]',
  },
  [ToasterType.Success]: {
    border: 'border-[#66BE9E]',
    background: 'bg-[#E6F4EF]',
    title: 'text-[#007D50]',
    supportingText: 'text-[#007D50]',
    icon: 'text-[#00935E]',
    iconOutline: 'outline-[#00935E]/20',
    iconBorder: 'border-[#00935E]',
    closeIcon: 'text-[#26A376]',
  },
};

const TOASTER_ICONS: Record<ToasterType, typeof CheckCircleIcon> = {
  [ToasterType.Default]: ExclamationCircleIcon,
  [ToasterType.Info]: ExclamationCircleIcon,
  [ToasterType.Error]: ExclamationCircleIcon,
  [ToasterType.Warning]: ExclamationCircleIcon,
  [ToasterType.Success]: CheckCircleIcon,
};

export interface ToasterProps {
  id: string;
  isVisible: boolean;
  type: ToasterType;
  title: string;
  message?: string;
  onClose?: () => void;
}

const CONTAINER_CLASSES =
  "pointer-events-auto font-[family-name:'Inter',sans-serif] flex gap-4 p-4 border rounded-xl max-w-[400px] " +
  'shadow-[0_4px_6px_-2px_rgba(16,24,40,0.03),0_12px_16px_-4px_rgba(16,24,40,0.08)]';

const ICON_CLASSES =
  'shrink-0 w-[20px] h-[20px] rounded-[25px] border-2 border-opacity-60 outline outline-2 outline-offset-2';

export const Toaster = ({ id, isVisible, type, title, message, onClose }: ToasterProps) => {
  const styles = TOASTER_STYLES[type];
  const Icon = TOASTER_ICONS[type];

  return (
    <Transition
      as={Fragment}
      appear
      show={isVisible}
      enter="transition ease-in-out duration-[500ms] transform"
      enterFrom="translate-x-full opacity-0"
      enterTo="translate-x-0 opacity-100"
      leave="transition ease-in-out duration-[500ms] transform"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo="translate-x-full opacity-0"
    >
      <div className={`${CONTAINER_CLASSES} ${styles.border} ${styles.background}`}>
        <div className="flex">
          <Icon
            className={`${ICON_CLASSES} ${styles.iconOutline} ${styles.iconBorder} ${styles.icon}`}
          />
        </div>
        <div className="flex flex-col flex-1 gap-1 text-sm">
          <label data-test-id={`${id}-toast-title`} className={`font-semibold ${styles.title}`}>
            {title}
          </label>
          {message && (
            <label data-test-id={`${id}-toast-sub-title`} className={styles.supportingText}>
              {message}
            </label>
          )}
        </div>
        <div className="flex">
          <XMarkIcon
            data-test-id={`${id}-x-button`}
            className={`shrink-0 w-[20px] h-[20px] cursor-pointer ${styles.closeIcon}`}
            onClick={onClose}
          />
        </div>
      </div>
    </Transition>
  );
};

export default Toaster;
